import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

export default function ShareBookingLinkModal({ isOpen, onClose }) {
  const [method, setMethod] = useState('email'); // 'email' or 'sms'
  const [recipient, setRecipient] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');
  const [bookingLink, setBookingLink] = useState('');
  const qrRef = useRef(null);

  // Fetch booking link when modal opens
  useEffect(() => {
    const fetchBookingLink = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/drivers/booking-link`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        setBookingLink(response.data.booking_link);
      } catch (error) {
        console.error('Error fetching booking link:', error);
      }
    };

    if (isOpen) {
      fetchBookingLink();
    }
  }, [isOpen]);

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Add padding around QR code
      const padding = 20;
      canvas.width = img.width + padding * 2;
      canvas.height = img.height + padding * 2;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, padding, padding);

      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = 'my-booking-qr-code.png';
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/send-booking-link`,
        {
          method,
          recipient
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      setStatus('success');
      setMessage(response.data.message);
      
      // Clear form after 2 seconds and close modal
      setTimeout(() => {
        setRecipient('');
        setStatus('idle');
        setMessage('');
        onClose();
      }, 2000);
      
    } catch (error) {
      setStatus('error');
      setMessage(
        error.response?.data?.detail || 
        'Failed to send booking link. Please try again.'
      );
    }
  };

  const handleCopyLink = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/drivers/booking-link`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      await navigator.clipboard.writeText(response.data.booking_link);
      setStatus('success');
      setMessage('Booking link copied to clipboard!');
      
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 2000);
      
    } catch (error) {
      setStatus('error');
      setMessage('Failed to copy link. Please try again.');
    }
  };

  const validateInput = () => {
    if (!recipient.trim()) return false;

    if (method === 'email') {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(recipient);
    } else {
      // Phone validation - must be 10 or 11 digits
      const digits = recipient.replace(/\D/g, '');
      return digits.length === 10 || digits.length === 11;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Invite Riders to Book</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* QR Code Section */}
        <div className="text-center mb-6">
          <p className="text-sm font-medium text-gray-700 mb-3">Scan here to book with me!</p>
          <div ref={qrRef} className="inline-block p-4 bg-white border-2 border-gray-200 rounded-lg">
            {bookingLink ? (
              <QRCodeSVG value={bookingLink} size={180} />
            ) : (
              <div className="w-[180px] h-[180px] bg-gray-100 animate-pulse rounded-lg" />
            )}
          </div>
          <button
            onClick={handleDownloadQR}
            disabled={!bookingLink}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download QR Code for Business Cards or for offline use!
          </button>
        </div>

        <div className="border-t border-gray-200 pt-4">
          {/* Copy Link Section */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              Copy your personal booking link:
            </p>
            <button
              onClick={handleCopyLink}
              className="w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition"
            >
              📋 Copy Link to Clipboard
            </button>
          </div>
        </div>

        <div className="border-t border-gray-200 my-4"></div>

        {/* Send Link Section */}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Send link via:
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => {
                  setMethod('email');
                  setRecipient('');
                  setStatus('idle');
                  setMessage('');
                }}
                className={`flex-1 py-2 px-4 rounded-md transition ${
                  method === 'email'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                📧 Email
              </button>
              <button
                type="button"
                disabled
                className="flex-1 py-2 px-4 rounded-md transition bg-gray-100 text-gray-400 cursor-not-allowed"
              >
                <span>📱 SMS</span>
                <span className="text-xs block">(Coming Soon)</span>
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {method === 'email' ? 'Email Address' : 'Phone Number'}
            </label>
            <input
              type={method === 'email' ? 'email' : 'tel'}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={
                method === 'email' 
                  ? 'rider@example.com' 
                  : '(555) 123-4567'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              required
            />
            {method === 'sms' && (
              <p className="text-xs text-gray-500 mt-1">
                Enter 10-digit US number or 11-digit with country code
              </p>
            )}
          </div>

          {status === 'success' && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-800">{message}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{message}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!validateInput() || status === 'loading'}
              className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'loading' ? 'Sending...' : 'Send Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}